import { useContext, useEffect, useState, FormEvent, useMemo } from "react";
import ControlledTable, { IColumn, ITableRowData } from "@/manager/components/controlled-table/controlled-table";
import AuthContext from "../../../../shared/context/auth-context";
import useHttp from "../../../../shared/hooks/use-http";
import { getAllData, deleteMultiple, postSingleNonTable, failedToast } from "../../../../shared/utils/api";
import TemplateModal from "../../global_templates/template-modal";
import TenantTemplateModalContent from "../tenant-template-modal-content";
import DatasetTemplatePopup from "../../global_templates/dataset-template-popup";
import ConfirmationPopup from "@/shared/components/confirmation-popup/confirmation-popup";

export type TemplateType = "analytics_report_template" | "analytics_report_pdf_template";

export type TenantModalRequestType =
  | "assign"
  | "delete"
  | "unarchive"
  | "assign_default"
  | "update_name"
  | "assign_global";

interface ITenantTemplateRow {
  id: string;
  name: string;
  is_global: boolean;
  is_installation: boolean;
  archived: boolean;
  sygno_default: boolean;
  tenant_default: boolean;
  current_tenant_default?: boolean;
  dataset_assigned?: boolean;
  layout: string;
}

export interface ITenantTemplateFormData {
  id: string;
  name: string | undefined;
  tenant_default: boolean | undefined;
  is_global: boolean | undefined;
  is_installation: boolean | undefined;
  archived: boolean | undefined;
  layout: string | undefined;
  current_tenant_default?: boolean;
}

interface IModalState {
  isOpen: boolean;
  rowId: string;
  request_type: TenantModalRequestType | undefined;
}

export interface ITenantTemplateTabProps {
  templateType: TemplateType;
}

const TENANT_TEMPLATE_COLUMNS: IColumn[] = [
  { key: "name", displayName: "Name", dataType: { type: "text" } },
  { key: "is_global", displayName: "Global", dataType: { type: "text" } },
];

const EMPTY_TEMPLATE_FORM_DATA: ITenantTemplateFormData = {
  id: "",
  name: "",
  tenant_default: undefined,
  is_global: undefined,
  is_installation: undefined,
  archived: undefined,
  layout: undefined,
  current_tenant_default: undefined,
};

const EMPTY_MODAL_STATE: IModalState = {
  isOpen: false,
  rowId: "",
  request_type: undefined,
};

const CONFIRM_BUTTON_LABELS: Record<TenantModalRequestType, string> = {
  unarchive: "Unarchive",
  assign: "Save",
  assign_default: "Save",
  assign_global: "Save",
  update_name: "Update",
  delete: "Confirm",
};


const TenantTemplateTab = ({ templateType }: ITenantTemplateTabProps) => {
  const [tableData, setTableData] = useState<ITableRowData[]>([]);
  const [rawRows, setRawRows] = useState<ITenantTemplateRow[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [sortedTemplates, setSortedTemplates] = useState("?order_by=name asc");
  const [validated, setValidated] = useState(false);
  const [modalState, setModalState] = useState<IModalState>(EMPTY_MODAL_STATE);
  const [templateFormData, setTemplateFormData] = useState<ITenantTemplateFormData>(EMPTY_TEMPLATE_FORM_DATA);
  const [datasetModal, setDatasetModal] = useState("");
  const [datasetModalVisible, setDatasetModalVisible] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [idsToBeDeleted, setIdsToBeDeleted] = useState<string[]>([]);


  const authCtx = useContext(AuthContext);
  const dataUrl = `tenant_templates/${templateType}?archived=true`;

  const { sendRequest: getAllTemplates, status: getAllTemplatesStatus, data: getAllTemplatesData } = useHttp(getAllData, true, authCtx.token, undefined, "analytics");
  const { sendRequest: deleteRequest, status: deleteStatus } = useHttp(deleteMultiple, false, authCtx.token, undefined, "analytics");
  const { sendRequest: sendTemplate, status: sendTemplateStatus } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");

  const fetchTemplateData = () => {
    getAllTemplates(dataUrl, `${sortedTemplates}&limit=50&offset=0`);
  };

  useEffect(() => {
    fetchTemplateData();
  }, []);


  useEffect(() => {
    if (getAllTemplatesStatus === "completed" && getAllTemplatesData) {
      setRawRows(getAllTemplatesData.rows ?? []);
      setTableData(
        (getAllTemplatesData.rows ?? []).map((row: ITenantTemplateRow) => ({
          id: row.id,
          name: row.name,
          is_global: row.is_global,
          is_installation: row.is_installation,
          archived: row.archived,
          sygno_default: row.sygno_default,
          tenant_default: row.tenant_default,
          current_tenant_default: row.current_tenant_default,
          dataset_assigned: row.dataset_assigned,
          layout: row.layout,
        }))
      );
    }
    if (getAllTemplatesStatus === "error") {
      failedToast("Failed to load templates. Please try again.");
    }
  }, [getAllTemplatesStatus, getAllTemplatesData]);

  useEffect(() => {
    if (deleteStatus === "completed") {
      closeModal();
      fetchTemplateData();
    }
    if (deleteStatus === "error") {
      failedToast("Failed to delete template. Please try again.");
    }
  }, [deleteStatus]);

  useEffect(() => {
    if (sendTemplateStatus === "completed") {
      fetchTemplateData();
      closeModal();
    }
    if (sendTemplateStatus === "error") {
      failedToast("Failed to update template. Please try again.");
    }
  }, [sendTemplateStatus]);


  const closeModal = () => {
    setModalState(EMPTY_MODAL_STATE);
    setTemplateFormData(EMPTY_TEMPLATE_FORM_DATA);
    setValidated(false);
  };

  const getRowById = (id: string) => rawRows.find((r) => r.id === id);
  const handleTemplateSubmit = (e?: FormEvent<HTMLFormElement>) => {
    if (e) {
      e.stopPropagation();
    }
    if (modalState.request_type === "update_name" && !templateFormData.name?.trim()) {
      setValidated(true);
      return;
    }

    const processMap = {
      update_name: "update_name",
      unarchive: "unarchive",
      assign_default: "update_name",
      assign_global: "update_name",
      assign: "assign",
      delete: "delete",
    };

    sendTemplate(`template_layout/${templateFormData.id}`, {
      id: templateFormData.id,
      name: templateFormData.name?.trim(),
      tenant_default: templateFormData.tenant_default,
      is_global: templateFormData.is_global,
      is_installation: templateFormData.is_installation,
      archived: templateFormData.archived,
      layout: templateFormData.layout,
      process: processMap[modalState.request_type!] ?? "update_name",
      template_type: templateType,
    });
  };

  const handleTenantDefaultSubmit = (e?: FormEvent<HTMLFormElement>) => {
    if (e) {
      e.stopPropagation();
    }

    sendTemplate(`template_layout/${templateFormData.id}`, {
      id: templateFormData.id,
      name: templateFormData.name?.trim(),
      tenant_default: templateFormData.tenant_default,
      is_global: templateFormData.is_global,
      is_installation: templateFormData.is_installation,
      archived: templateFormData.archived,
      layout: templateFormData.layout,
      process: "tenant_default",
      template_type: templateType,
    });
  };

  const handleDelete = (id: string) => {
    deleteRequest(`tenant_templates/${templateType}?archived=true`, { ids: [id] });
  };

  const handleModalConfirm = () => {
    const { request_type, rowId } = modalState;
    if (!rowId){
      return;
    }
    switch (request_type) {
      case "delete":
        handleDelete(rowId);
        break;
      case "assign":
        handleTenantDefaultSubmit();
        break;
      case "update_name":
      case "unarchive":
      case "assign_default":
      case "assign_global":
        handleTemplateSubmit();
        break;
      default:
        break;
    }
  };
  
  const handleOnDeleteConfirmSubmit = () => {
    deleteRequest(`template_layouts/${templateType}`, { ids: idsToBeDeleted });
  };

  const onTableScroll = () => {
    if (rawRows.length >= 50) {
      const nextOffset = currentOffset + 1;
      setCurrentOffset(nextOffset);
      getAllTemplates(dataUrl, `${sortedTemplates}&limit=50&offset=${nextOffset * 50}`);
    }
  };

  const sortBy = ({ column, direction }: { column: string | undefined; direction: string | undefined }) => {
    if (!column || !direction) {
      return;
    }
    const sortingQuery = `?order_by=${column} ${direction}`;
    setSortedTemplates(sortingQuery);
    setCurrentOffset(0);
    getAllTemplates(dataUrl, `${sortingQuery}&limit=50&offset=0`);
  };

  const handleOnDeleteClick = (ids: string[]) => {
    const hasSygnoDefault = ids.some((id) => getRowById(id)?.sygno_default);
    if (hasSygnoDefault) {
      failedToast("You cannot delete the Sygno default templates.");
      return;
    }
    setIdsToBeDeleted(ids);
    setShowConfirmationPopup(true);
  };


  const handleRowClick = (id: string) => {
    const row = getRowById(id);
    if (row?.is_global || row?.archived) {
      return;
    }

    setModalState({ isOpen: true, rowId: id, request_type: "update_name" });
    setTemplateFormData({
      id,
      name: row?.name ?? "",
      tenant_default: row?.tenant_default,
      is_global: row?.is_global,
      is_installation: row?.is_installation,
      archived: row?.archived,
      layout: row?.layout,
    });
  };

  const handleActionClick = (actionKey: string, rowId: string) => {
    const row = getRowById(rowId);

    if (actionKey === "view_datasets") {
      setDatasetModal(rowId);
      setDatasetModalVisible(true);
      return;
    }

    if (actionKey === "tenant_default") {
      if (row?.archived || (row?.current_tenant_default && row?.sygno_default)) {
        return;
      }
      setModalState({ isOpen: true, rowId, request_type: "assign" });
      setTemplateFormData({
        id: rowId,
        name: row?.name,
        tenant_default: !row?.current_tenant_default,
        is_global: row?.is_global,
        is_installation: row?.is_installation,
        archived: row?.archived,
        layout: row?.layout,
        current_tenant_default: row?.current_tenant_default,
      });
      return;
    }

    if (actionKey === "archive_delete") {
      setModalState({ isOpen: true, rowId, request_type: "delete" });
      return;
    }

    if (actionKey === "unarchive") {
      setModalState({ isOpen: true, rowId, request_type: "unarchive" });
      setTemplateFormData({
        id: rowId,
        name: row?.name,
        tenant_default: row?.tenant_default,
        is_global: row?.is_global,
        is_installation: row?.is_installation,
        archived: false,
        layout: row?.layout,
      });
    }
  };

  const actionColumns: IColumn[] = useMemo(() => [
    {
      key: "view_datasets",
      displayName: "Related datasets",
      dataType: {
        type: "action",
        buttonText: "View datasets",
        buttonStyle: "none",
        shouldShow: (row: ITableRowData) => !!row.dataset_assigned,
        displayName: (_row: ITableRowData) => (
          <button className="templateList-viewDatasetsButton btn btn-secondary">
            View related datasets
          </button>
        ),
      },
    },
    {
      key: "tenant_default",
      displayName: "Default",
      dataType: {
        type: "action",
        buttonStyle: "none",
        buttonText: "",
        shouldShow: (row: ITableRowData) => !row.archived,
        displayName: (row: ITableRowData) => (
          <button className="btn btn-secondary" disabled={row.archived || (row.current_tenant_default && row.sygno_default)}>
            {row.current_tenant_default ? "★" : "☆"}
          </button>
        ),
      },
    },
    {
      key: "unarchive",
      displayName: "",
      dataType: {
        type: "action",
        buttonStyle: "none",
        buttonText: "",
        shouldShow: (row: ITableRowData) => !row.is_global && !!row.archived,
        displayName: (_row: ITableRowData) => (
          <button className="templateList-archiveButton btn btn-primary">
            Unarchive Template
          </button>
        ),
      },
    },
  ], []);

  return (
    <div className="templateList">
      <ConfirmationPopup
        confirmButtonText="Delete"
        text="Are you sure you want to delete/archive the selected rows?"
        show={showConfirmationPopup}
        handlePopupClosed={() => setShowConfirmationPopup(false)}
        handleSaveClick={handleOnDeleteConfirmSubmit}
      />
      <ControlledTable
        columns={[...TENANT_TEMPLATE_COLUMNS, ...actionColumns]}
        data={tableData}
        markedRows={tableData
          .filter((row) => row.archived)
          .map((row) => ({ id: row.id, mark: "faded" as const }))}
        selectionMode="multi"
        onSorted={sortBy}
        onScroll={onTableScroll}
        onRowClick={handleRowClick}
        onActionClick={handleActionClick}
        onDeleted={handleOnDeleteClick}
      />

      {modalState.isOpen && (
        <TemplateModal
          onHide={closeModal}
          title={modalState.request_type === "update_name" ? "Update Template" : "Confirm Action"}
          confirmButtonText={
            modalState.request_type
              ? CONFIRM_BUTTON_LABELS[modalState.request_type]
              : "Confirm"
          }
          onConfirm={handleModalConfirm}
        >
          <TenantTemplateModalContent
            request_type={modalState.request_type}
            rowId={modalState.rowId}
            templateFormData={templateFormData}
            validated={validated}
            onFormDataChange={setTemplateFormData}
            onSubmit={handleTemplateSubmit}
            onTenantDefaultSubmit={handleTenantDefaultSubmit}
            rawRows={rawRows}
          />
        </TemplateModal>
      )}

      {datasetModalVisible && (
        <DatasetTemplatePopup
          handlePopupClosed={() => setDatasetModalVisible(false)}
          row_id={datasetModal}
          columns={[
            { key: "dataset_name", displayName: "Dataset name", dataType: { type: "text" } },
          ]}
          show={datasetModalVisible}
          global_page={false}
        />
      )}
    </div>
  );
};

export default TenantTemplateTab;